import {
  getLeoUuid,
  LeoUUID,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { EmbedDetail, EmbedDetailDTO } from "./embedDetail";
/* eslint-disable import/export */
export abstract class DeleteEmbedRPC implements
  LeoRPC<DeleteEmbedRPC.Request, DeleteEmbedRPC.Response, DeleteEmbedRPC.Errors.Errors>
{
  abstract execute(
    request: DeleteEmbedRPC.Request
  ): Promise<LeoRPCResult<DeleteEmbedRPC.Response, DeleteEmbedRPC.Errors.Errors>>;
}

export namespace DeleteEmbedRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    embedId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isDeleteEmbedRPCRequest: boolean = true;
  
    readonly embedId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    embedId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.embedId = embedId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const embedId = getLeoUuid(dto, "embedId", InvalidRequestEmbedIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        embedId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          embedId: this.embedId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `DeleteEmbedRPC(embedId='${this.embedId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    embedDetails: EmbedDetailDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isDeleteEmbedResponse: boolean = true;
  
    readonly embedDetails: EmbedDetail[];
  
  
    constructor(
    embedDetails: EmbedDetail[]
    ) {
      this.embedDetails = embedDetails;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const embedDetails = getList<EmbedDetailDTO, EmbedDetail>(
        dto,
        "embedDetails",
        EmbedDetail.fromDTO,
        InvalidResponseEmbedDetailsError
      );
      return new Response(
        embedDetails
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          embedDetails: this.embedDetails.map((e: EmbedDetail) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `DeleteEmbed(embedDetails='${this.embedDetails}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidEmbedId = "INVALID_EMBED_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `DeleteEmbedRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `DeleteEmbedRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidEmbedIdDTO {
      code: string;
    }
    
    export class InvalidEmbedId extends LeoRPCError<RPCError> {
      readonly isInvalidEmbedId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidEmbedId;
        super(code);
      }
    
    
      toDTO(): InvalidEmbedIdDTO {
        const dto: InvalidEmbedIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidEmbedId.prototype.toString = function toString(): string {
      return `DeleteEmbedRPC.INVALID_EMBED_ID()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | InvalidEmbedId;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | InvalidEmbedIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidEmbedId:
          return new InvalidEmbedId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestEmbedIdError extends DeleteEmbedRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmbedIdError";
  }
}

export class InvalidRequestProjectIdError extends DeleteEmbedRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseEmbedDetailsError extends DeleteEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseEmbedDetailsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends DeleteEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends DeleteEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidEmbedIdError extends DeleteEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidEmbedIdError";
  }
}

